/**
 * Debt, second edition: what is owed, how fast it is coming down, the queue it
 * is paid off in, and a plan with an overpayment.
 *
 * Four figures; a chart of the balance month by month with twelve months
 * ahead (instalments alone, and the plan inside them); the payoff queue with a
 * payment and the edit form in place; and the plan itself. Written once for
 * both layouts (`compact` is the phone).
 */

import { h } from '../vdom.js';
import { localDay, numberText, shiftMonth, parseNumber, debtBalanceAt, debtLatestBalance, currentMonth } from '../../../shared/model.js';
import { debtPlan, debtQueueAt } from '../../../shared/compute.js';
import { C, MONO, css, tap, input, select, emptyLine, debtChange, currencyOptions, saveFile } from './common.js';
import { buildIcs, monthlyRule, nextPayDate } from '../ics.js';
import { pill, segmented } from './cashflow.js';
import { DANGER_TEXT, DANGER_BG, DANGER_LINE, GAIN_BG, GAIN_LINE, GAIN_LABEL } from './whatif.js';

const pct = ratio => (Math.max(0, Math.min(1, ratio)) * 100).toFixed(1) + '%';
const PAST = 'rgba(239,78,67,.32)';
const PLAN = '#2B6B57';

const box = (compact, style, ...children) => h('div', {
  style: css({
    padding: compact ? '14px' : '18px 20px', border: `1px solid ${C.stroke}`, borderRadius: compact ? '16px' : '14px',
    background: C.card, display: 'flex', flexDirection: 'column', gap: compact ? '12px' : '16px', minWidth: 0, ...style
  })
}, ...children);
const title = (compact, text) => h('div', { style: css({ fontSize: compact ? '14px' : '15px', fontWeight: 700 }) }, text);
const hint = text => h('div', { style: css({ fontSize: '12.5px', color: C.muted }) }, text);
const mono = (text, style = {}) => h('span', { style: css({ fontFamily: MONO, ...style }) }, text);

/**
 * Today's month: the last bar of the chart window that is not projected, or
 * the calendar when the window on screen lies wholly in the past or ahead.
 */
export function debtToday(metrics) {
  const points = metrics.debtBars || [];
  const ahead = points.findIndex(point => point.projected);
  if (ahead > 0) return points[ahead - 1].month;
  if (ahead === 0) return shiftMonth(points[0].month, -1);
  return currentMonth();
}

/**
 * The plan and the instalments-only run, always from today.
 *
 * Both used to start from the month on screen, so clicking August and then
 * September gave two different futures: the balances, an instalment worked
 * out from the term, the smallest-first order and the rates were all that
 * month's. The future is one; only the past changes with the month you pick.
 */
export function debtPlans(metrics, state) {
  const today = debtToday(metrics);
  const queue = debtQueueAt(state.data || {}, today, {
    strategy: state.settings?.debtStrategy === 'balance' ? 'balance' : 'rate',
    baseCurrency: state.data?.settings?.baseCurrency || state.settings?.baseCurrency
  });
  return {
    today,
    queue,
    base: debtPlan({ debts: queue, extra: 0, rollover: false }),
    plan: debtPlan({ debts: queue, extra: state.debtExtra || 0, rollover: true })
  };
}

// ------------------------------------------------------------- figures ---

export function debtKpis({ metrics, t, fmt, state, compact = false, plans }) {
  const s = t.shell;
  const k = metrics.totals;
  const rows = metrics.debtRows || [];
  const open = rows.filter(row => row.balance > 0);
  const noRata = open.filter(row => row.kind !== 'card' && !(row.scheduledPayment > 0));
  /* In the base currency, as the balance beside it is: a sum of each debt's own figure mixed euros with złoty. */
  const original = k.debtOriginal ?? rows.reduce((sum, row) => sum + (row.original || 0), 0);
  const paidRatio = k.debtPaidPct || 0;
  const { base } = plans || debtPlans(metrics, state);

  const tile = (label, value, sub, { color = C.text, alarm = false, extra = null } = {}) => h('div', {
    style: css({
      padding: compact ? '12px 13px' : '16px 18px', borderRadius: '14px', minWidth: 0,
      border: `1px solid ${alarm ? DANGER_LINE : C.stroke}`, background: alarm ? DANGER_BG : C.card,
      display: 'flex', flexDirection: 'column', gap: compact ? '5px' : '7px'
    })
  },
    h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '10px' : '10.5px', letterSpacing: '1.2px', color: alarm ? '#D9A9AE' : C.muted }) }, label),
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '12px' }) },
      h('div', { style: css({ fontFamily: MONO, fontSize: compact ? '18px' : '26px', fontWeight: 700, letterSpacing: compact ? 0 : '-0.6px', color, whiteSpace: 'nowrap' }) }, value),
      extra),
    h('div', { style: css({ fontSize: compact ? '11.5px' : '12.5px', color: alarm ? '#C9B4B7' : C.textDim, lineHeight: 1.4 }) }, sub)
  );

  return h('div', {
    style: css({ display: 'grid', gap: compact ? '10px' : '14px', gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(auto-fit, minmax(210px, 1fr))' })
  },
    tile(s.dbLeft, fmt.money(k.debtTotal), s.dbLeftSub(open.length), { color: k.debtTotal > 0 ? DANGER_TEXT : C.text, alarm: k.debtTotal > 0 }),
    tile(s.dbPaid, Math.round(paidRatio * 100) + '%', s.dbPaidSub(fmt.money(Math.max(0, original - k.debtTotal)), fmt.money(original)), {
      color: C.accent,
      extra: !compact && h('div', { key: 'bar', style: css({ flex: 1, height: '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
        h('div', { style: css({ width: pct(paidRatio), height: '100%', borderRadius: '3px', background: C.accent }) }))
    }),
    /* "Every debt has a set instalment" is true of no debts at all, and reads as if there were some. */
    tile(s.dbRata, fmt.money(k.debtPayments), !open.length ? s.dbNoDebtsSub : noRata.length ? s.dbNoRata(noRata.length) : s.dbAllRata),
    tile(s.dbInt12, fmt.money(base.interest12), s.dbInt12Sub(fmt.money(base.interest12 / 12)))
  );
}

// --------------------------------------------------------------- chart ---

/**
 * The balance at the end of each month on the books up to today, and twelve
 * months on from today: the dashed outline is the instalments alone, the
 * green inside it the plan with the overpayment. The month on screen is the
 * red one, and every bar is a link to its month.
 *
 * The axis is anchored on today, not on the month on screen. Anchored on the
 * month on screen, going back a month turned every later month into forecast
 * that could not be clicked: you could step back but never forward again.
 */
export function debtChart({ metrics, t, fmt, state, actions, compact = false, plans }) {
  const s = t.shell;
  const k = metrics.totals;
  const { base, plan, today } = plans || debtPlans(metrics, state);
  /*
   * The months up to today, from both windows: the one around the month on
   * screen and the one around today. A month picked a year ahead has a window
   * that starts after today, and the chart then had no history at all -- its
   * one "today" bar fell back to the picked month's total. A past month's total
   * is the same whichever window it comes from.
   */
  const seen = new Map();
  for (const point of [...(state.metricsNow?.debtBars || []), ...(metrics.debtBars || [])]) {
    if (point.month <= today && !seen.has(point.month)) seen.set(point.month, point);
  }
  const book = [...seen.values()].sort((a, b) => a.month.localeCompare(b.month));
  const first = book.findIndex(point => point.total > 0);
  const kept = first === -1 ? [] : book.slice(first);
  /* A year of history on a wide screen, half a year on a phone: years of entries would shrink the bars to slivers. */
  const room = compact ? 6 : 12;
  const chosen = kept.findIndex(point => point.month === state.month);
  const from = Math.max(0, Math.min(kept.length - room, chosen === -1 ? kept.length : chosen));
  const recorded = kept.slice(from, from + room);
  if (!recorded.length && !((state.metricsNow || metrics).totals.debtTotal > 0) && !(k.debtTotal > 0)) return null;

  const at = (series, i) => (series.totals[i] !== undefined ? series.totals[i] : (series.months === null ? series.totals[series.totals.length - 1] || 0 : 0));
  const kindOf = (month, kind) => (month === state.month ? 'now' : kind);
  const bars = [
    ...recorded.map(point => ({ month: point.month, value: point.total, kind: kindOf(point.month, 'past') })),
    ...(recorded.length ? [] : [{ month: today, value: (state.metricsNow || metrics).totals.debtTotal, kind: kindOf(today, 'past') }]),
    ...Array.from({ length: 12 }, (_, i) => ({ month: shiftMonth(today, i + 1), value: at(base, i), plan: at(plan, i), kind: kindOf(shiftMonth(today, i + 1), 'next') }))
  ];
  const anchor = Math.max(0, bars.findIndex(bar => bar.month === today));
  const picked = bars.findIndex(bar => bar.kind === 'now');
  /*
   * A phone names every third month, counted from today, plus the one on
   * screen with room around it: three letters do not fit a 16px column.
   */
  const named = i => !compact || i === picked || ((i - anchor) % 3 === 0 && (picked === -1 || Math.abs(i - picked) > 1));
  const top = Math.max(1, ...bars.map(bar => bar.value));
  /*
   * Every bar keeps its amount. In thousands, one scale for the whole chart,
   * so "37,7K" fits where "37,7 tys." ran into its neighbours.
   */
  const thousands = top >= 1000;
  const short = value => (thousands ? fmt.number(value / 1000, top >= 100000 ? 0 : 1) + 'K' : fmt.number(Math.round(value), 0));
  /* January carries its year on a line of its own: "STY '27" across was wider than a column. */
  const label = month => {
    const name = t.months[parseInt(month.slice(5, 7), 10) - 1].toUpperCase();
    return month.slice(5, 7) === '01'
      ? [name, h('span', { key: 'y', style: css({ fontSize: compact ? '7.5px' : '9px', opacity: 0.8 }) }, "'" + month.slice(2, 4))]
      : [name];
  };

  const still = Math.abs(k.debtChange) < 0.5;
  const change = !k.hasPrevDebt ? t.fx.firstMonth
    : still ? t.fx.debtSame(k.prevDebtMonth) : t.fx.vsMonth(fmt.signed(k.debtChange), k.prevDebtMonth);
  const changeColor = !k.hasPrevDebt || still ? C.muted : (k.debtChange < 0 ? C.accent : DANGER_TEXT);

  return box(compact, {},
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 220px', minWidth: 0 }) },
        title(compact, s.dbChart), hint(s.dbChartSub)),
      mono(change, { fontSize: '13px', fontWeight: 700, color: changeColor })
    ),
    h('div', {
      style: css({
        display: 'grid', gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))`, gap: compact ? '2px' : '4px',
        alignItems: 'end', height: compact ? '150px' : '190px', containerType: 'inline-size'
      })
    }, ...bars.map((bar, i) => {
      const height = (Math.max(bar.value / top * 100, bar.value > 0 ? 2 : 0) * 0.82).toFixed(1) + '%';
      const ahead = bar.month > today;
      return h('div', {
        key: 'b' + bar.month,
        ...tap(() => actions.goToMonth(bar.month), s.monthFull(bar.month)),
        title: ahead
          ? s.monthFull(bar.month) + ': ' + s.dbLegBase + ' ' + fmt.money(bar.value) + ' · ' + s.dbLegPlan + ' ' + fmt.money(bar.plan)
          : s.monthFull(bar.month) + ': ' + fmt.money(bar.value),
        style: css({
          height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center',
          gap: '6px', minWidth: 0, cursor: 'pointer'
        })
      },
        mono(short(ahead ? bar.plan : bar.value), {
          key: 'v', whiteSpace: 'nowrap', lineHeight: 1, fontWeight: bar.kind === 'now' ? 700 : 400,
          color: bar.kind === 'now' ? DANGER_TEXT : ahead ? '#7FB8A5' : C.textDim,
          /* A phone's column is too narrow for the amount across, so it stands upright; a wide one scales it to the column. */
          ...(compact
            ? { fontSize: '9px', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }
            : { fontSize: `clamp(7px, ${(28 / bars.length).toFixed(2)}cqi, 10px)`, letterSpacing: '-0.2px' })
        }),
        h('div', {
          style: css({
            position: 'relative', width: '100%', height, borderRadius: '4px 4px 2px 2px',
            background: ahead ? 'transparent' : bar.kind === 'now' ? C.danger : PAST,
            border: ahead ? `1px dashed ${bar.kind === 'now' ? DANGER_TEXT : C.strokeHi}` : 'none', boxSizing: 'border-box'
          })
        }, ahead && h('div', {
          key: 'plan',
          style: css({
            position: 'absolute', left: 0, right: 0, bottom: 0, borderRadius: '3px 3px 1px 1px', background: PLAN,
            height: bar.value > 0 ? pct(bar.plan / bar.value) : '0%'
          })
        }))
      );
    })),
    h('div', {
      style: css({ display: 'grid', gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))`, gap: compact ? '2px' : '4px', marginTop: '-8px' })
    }, ...bars.map((bar, i) => h('span', {
      key: 'l' + bar.month,
      style: css({
        textAlign: 'center', fontFamily: MONO, fontSize: compact ? '8.5px' : '10px', letterSpacing: '.3px', whiteSpace: 'nowrap',
        /* A phone names one column in three, so a name may spill into its empty neighbours. */
        overflow: compact ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25,
        color: bar.kind === 'now' ? DANGER_TEXT : bar.month > today ? C.faint : C.muted, fontWeight: bar.kind === 'now' ? 700 : 400
      })
    }, ...(named(i) ? label(bar.month) : [])))),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', fontSize: '12px', color: C.muted }) },
      legend({ background: PAST }, s.dbLegRecorded),
      legend({ border: `1px dashed ${C.ghost}` }, s.dbLegBase),
      legend({ background: PLAN }, s.dbLegPlan))
  );
}
const legend = (swatch, text) => h('span', { style: css({ display: 'flex', alignItems: 'center', gap: '7px' }) },
  h('span', { style: css({ width: '10px', height: '10px', borderRadius: '2px', boxSizing: 'border-box', ...swatch }) }), text);

// --------------------------------------------------------------- queue ---

export function debtQueue({ metrics, t, fmt, state, inputs, actions, compact = false }) {
  const s = t.shell;
  const ui = state.debtUi;
  const k = metrics.totals;
  const rows = metrics.debtRows || [];
  const strategy = state.settings?.debtStrategy === 'balance' ? 'balance' : 'rate';
  const monthOf = n => fmt.monthName(shiftMonth(state.month, n));
  const rateTone = rate => (rate >= 12 ? [DANGER_BG, DANGER_TEXT] : rate >= 5 ? ['rgba(227,194,122,.12)', C.gold] : [GAIN_BG, C.accentHi]);

  const startEdit = row => {
    actions.setDebtUi({ editId: row.id, formOpen: true, payId: null, kind: row.kind === 'card' ? 'card' : 'loan', rev: ui.rev + 1 });
    if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined') {
      requestAnimationFrame(() => document.getElementById('debt-form')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };

  const line = row => {
    const tone = rateTone(row.rate);
    const paying = ui.payId === row.id;
    const moved = debtChange(row, k.prevDebtMonth, t, fmt);
    let when;
    let whenColor = C.textDim;
    if (row.balance <= 0) { when = s.dbPaidOff; whenColor = C.accent; }
    else if (row.kind === 'card') when = s.dbCardRow;
    else if (!(row.scheduledPayment > 0)) { when = s.dbNoRataRow; whenColor = C.gold; }
    else if (row.monthsLeft) when = s.dbMonthsLeft(row.monthsLeft, monthOf(row.monthsLeft));
    else { when = t.fx.interestOutpaces; whenColor = DANGER_TEXT; }

    const submitPayment = () => {
      const typed = parseNumber(inputs.value('pay_' + row.id));
      const amount = typed || row.scheduledPayment;
      if (!amount || amount <= 0) return actions.showError(s.amountNeeded);
      actions.payDebt(row.id, amount);
      inputs.clear(['pay_' + row.id]);
      actions.setDebtUi({ payId: null });
    };

    return h('div', {
      key: row.id,
      style: css({ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${C.line}`, background: ui.editId === row.id ? C.panel : 'transparent' })
    },
      h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: compact ? '10px 14px' : '12px 24px', padding: '14px 4px' }) },
        h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 240px', minWidth: 0 }) },
          h('span', {
            style: css({
              flex: '0 0 auto', width: '30px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '6px', border: `1px solid ${GAIN_LINE}`, background: GAIN_BG, fontFamily: MONO, fontSize: '11px', fontWeight: 700, color: C.accentHi
            })
          }, '#' + row.rank),
          h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }) },
            h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }) },
              h('span', { style: css({ fontSize: '14.5px', fontWeight: 600 }) }, row.name),
              h('span', {
                style: css({ padding: '2px 7px', borderRadius: '5px', background: tone[0], fontFamily: MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.4px', color: tone[1] })
              }, fmt.number(row.rate, 1) + '%'),
              row.kind === 'card' && h('span', {
                key: 'card', style: css({ padding: '2px 7px', borderRadius: '5px', border: `1px solid ${C.strokeHi}`, fontFamily: MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.4px', color: C.textDim })
              }, s.dbCardTag)),
            h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.muted }) },
              s.dbRepaid(Math.round(row.repaidRatio * 100) + '%', fmt.money(row.repaid, 0, row.currency), fmt.money(row.original, 0, row.currency))
              + (row.fees ? ' · ' + s.dbFee(fmt.money(row.fees, 0, row.currency)) : '')),
            moved.text && h('span', { key: 'mv', style: css({ fontFamily: MONO, fontSize: '11px', color: moved.color }) }, moved.text)
          )
        ),
        h('div', { style: css({ flex: '2 1 220px', minWidth: compact ? '100%' : '180px', display: 'flex', flexDirection: 'column', gap: '7px' }) },
          h('div', { style: css({ height: '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
            h('div', { style: css({ width: pct(row.repaidRatio), height: '100%', borderRadius: '3px', background: C.accent }) })),
          h('div', { style: css({ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px', flexWrap: 'wrap' }) },
            h('span', { style: css({ color: whenColor }) }, when),
            row.totalInterest > 0.5 && h('span', { key: 'i', style: css({ color: C.muted, whiteSpace: 'nowrap' }) }, s.dbInterest(fmt.money(row.totalInterest, 0, row.currency))))
        ),
        h('div', { style: css({ flex: '0 0 auto', minWidth: '110px', display: 'flex', flexDirection: 'column', alignItems: compact ? 'flex-start' : 'flex-end', gap: '2px' }) },
          mono(fmt.money(row.balance, 0, row.currency), { fontSize: '17px', fontWeight: 700, whiteSpace: 'nowrap' }),
          h('span', { style: css({ fontSize: '11.5px', color: C.muted }) },
            row.scheduledPayment > 0
              ? s.dbRataLine(fmt.money(row.scheduledPayment, 0, row.currency)) + (row.payDay ? ' · ' + s.dbPayDayShort(row.payDay) : '') + (row.paymentDerived ? ' · ' + s.dbRataDerived : '')
              : row.kind === 'card' ? s.dbCardShort : s.dbNoRataShort)),
        h('div', { style: css({ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '4px', marginLeft: compact ? 'auto' : 0 }) },
          /*
           * Not in a month after today. A payment is logged when it is made; filed
           * ahead, it became a record the month's projection could not see past,
           * and a correction to the month before never reached it.
           */
          k.debtProjected
            ? h('div', { key: 'pay', style: css({ opacity: 0.45 }) }, pill(s.dbPayOpen, () => actions.showError(s.dbPayFuture), { key: 'pay', title: s.dbPayFuture }))
            : pill(paying ? s.dbPayClose : s.dbPayOpen, () => actions.setDebtUi({ payId: paying ? null : row.id }), { primary: false, muted: paying, key: 'pay' }),
          h('div', {
            ...tap(() => startEdit(row), s.editDebtTitle), class: 'pt-button', title: s.editDebtTitle,
            style: css({ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: `1px solid ${C.stroke}`, color: C.muted, fontSize: '12px', cursor: 'pointer' })
          }, '✎'),
          h('div', {
            ...tap(() => actions.removeDebt(row.id), s.removeLabel), class: 'pt-button', title: s.removeLabel,
            style: css({ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.faint, fontSize: '16px', cursor: 'pointer' })
          }, '×'))
      ),
      paying && !k.debtProjected && h('div', {
        key: 'payrow', class: 'pt-rise',
        style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: compact ? '0 4px 14px' : '0 4px 14px 46px' })
      },
        h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.dbPayIn(state.month)),
        h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', width: '180px', flex: compact ? '1 1 140px' : '0 0 auto' }) },
          input(inputs, 'pay_' + row.id, String(Math.round(row.scheduledPayment || 0)), {
            type: 'number', onEnter: submitPayment, ariaLabel: s.dbPayIn(state.month),
            style: { height: '40px', borderRadius: '10px', border: `1px solid ${C.accent}`, fontFamily: MONO, fontSize: '15px', fontWeight: 700, flex: 1 }
          }),
          mono(row.currency, { fontSize: '12px', color: C.muted })),
        pill(t.logPayment, submitPayment, { primary: true, key: 'save' }),
        h('span', { style: css({ fontSize: '12px', color: C.faint }) }, s.dbPayHint))
    );
  };

  return box(compact, { gap: '6px', padding: compact ? '14px' : '16px 20px 18px' },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', paddingBottom: '6px' }) },
      title(compact, t.payoffQueue),
      hint(strategy === 'balance' ? s.dbQueueBalance : s.dbQueueRate),
      /* After today the balances are worked out, not written down -- said where they are read. */
      k.debtProjected && h('span', {
        key: 'proj', title: s.dbProjectedTitle,
        style: css({ padding: '2px 8px', borderRadius: '5px', border: `1px dashed ${C.strokeHi}`, fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.6px', color: C.textDim })
      }, s.dbProjected),
      h('div', { style: css({ flex: 1 }) }),
      mono(s.dbCount(rows.length), { fontSize: '12.5px', color: C.muted }),
      rows.length > 0 && pill(s.icsButton, () => {
        /* From today's figures, whichever month is on screen -- a calendar is about what comes next. */
        const events = debtCalendarEvents((state.metricsNow || metrics).debtRows || [], { t, fmt });
        if (!events.length) return actions.showError(s.dbIcsNone);
        saveFile(s.dbIcsFile, buildIcs(events, { name: s.dbIcsCalendar }), 'text/calendar');
      }, { key: 'ics', title: s.dbIcsTitle })),
    rows.length === 0
      ? h('div', { key: 'empty', style: css({ padding: '8px 0' }) }, emptyLine(s.dbEmpty))
      : h('div', { key: 'list', style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) }, ...rows.map(line)),
    (ui.formOpen || ui.editId)
      ? debtForm({ t, fmt, state, inputs, actions, compact })
      : h('div', {
        key: 'open', ...tap(() => actions.setDebtUi({ formOpen: true, editId: null, kind: 'loan', rev: ui.rev + 1 }), s.dbNew), class: 'pt-button',
        style: css({
          marginTop: '12px', minHeight: '64px', padding: '12px 16px', border: `1px dashed ${C.strokeHi}`, borderRadius: '12px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', textAlign: 'center', cursor: 'pointer'
        })
      },
        mono(s.dbNew, { fontSize: '12.5px', fontWeight: 700, letterSpacing: '1.4px', color: C.accent }),
        h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.dbNewHint))
  );
}

/**
 * The instalments as calendar events: one repeating event per debt that has a
 * payment day and an instalment, from its next due day. A loan's series ends
 * with its last instalment; a card's runs on, since its balance has no schedule.
 */
export function debtCalendarEvents(rows, { t, fmt, from = localDay() }) {
  const s = t.shell;
  return rows
    .filter(row => row.balance > 0 && row.payDay && row.scheduledPayment > 0)
    .map(row => {
      const count = row.kind === 'loan' && row.monthsLeft > 0 ? row.monthsLeft : null;
      return {
        uid: 'debt-' + row.id,
        date: nextPayDate(row.payDay, from),
        summary: s.dbIcsSummary(row.name, fmt.money(row.scheduledPayment, 2, row.currency)),
        description: count ? s.dbIcsLeft(count) : s.icsFrom,
        rrule: monthlyRule(row.payDay, count),
        /* The evening before is too late to move money; the morning before is not. */
        alarm: '-PT15H'
      };
    });
}

/** A new debt, or the one whose ✎ was pressed -- with the dialog's rules for the balance. */
function debtForm({ t, fmt, state, inputs, actions, compact }) {
  const s = t.shell;
  const ui = state.debtUi;
  const editing = ui.editId ? (state.data?.debts || []).find(row => row.id === ui.editId) : null;
  /* The balance as it stood in the month on screen -- what a change to it is a statement about. */
  const shown = editing ? (debtBalanceAt(editing, state.month) ?? debtLatestBalance(editing)) : null;
  const read = name => parseNumber(inputs.value(name));
  const close = () => actions.setDebtUi({ formOpen: false, editId: null, rev: ui.rev + 1 });
  const kind = ui.kind === 'card' ? 'card' : 'loan';

  const save = () => {
    const name = inputs.value('dbName').trim();
    const balance = read('dbBal');
    if (!name) return actions.showError(s.labelNeeded);
    if (balance === null || balance < 0) return actions.showError(s.amountNeeded);
    const values = { rate: read('dbRate'), payment: read('dbRata'), original: read('dbOrig'), fees: read('dbFee'), term: read('dbMonths') };
    for (const value of Object.values(values)) if (value !== null && value < 0) return actions.showError(s.numberNeeded);
    const payDay = read('dbDay');
    if (payDay !== null && (payDay < 1 || payDay > 31 || Math.round(payDay) !== payDay)) return actions.showError(s.dbPayDayNeeded);
    const currency = inputs.value('dbCur') || (editing ? editing.currency : actions.entryCurrency());
    const fields = {
      name,
      original: values.original === null ? balance : values.original,
      rate: values.rate || 0,
      payment: values.payment || 0,
      fees: values.fees || 0,
      /* A card has no term: what is owed on it moves with spending, not with a schedule. */
      term: kind === 'loan' && values.term && values.term > 0 ? Math.round(values.term) : null,
      currency,
      kind,
      payDay: payDay || null
    };
    if (editing) {
      /* Sent only when it changed: renaming a loan while looking at March says nothing about March. */
      const patch = { ...fields };
      if (Math.abs(balance - shown) >= 0.005) patch.balance = balance;
      actions.saveEdit('debt', editing.id, patch);
    } else {
      actions.addDebt({ ...fields, balance });
    }
    inputs.clear(['dbName', 'dbBal', 'dbOrig', 'dbRate', 'dbRata', 'dbMonths', 'dbFee', 'dbDay']);
    close();
  };

  const field = (name, label, value, unit, { wide = false } = {}) => h('label', {
    key: name,
    style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, ...(wide ? { gridColumn: '1 / -1' } : {}) })
  },
    h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, label),
    h('div', {
      style: css({ display: 'flex', alignItems: 'center', gap: '6px', height: '42px', border: `1px solid ${C.strokeHi}`, borderRadius: '10px', background: C.panel, padding: '0 12px' })
    },
      input(inputs, name, wide ? s.dbLenderPh : '0', {
        type: wide ? 'text' : 'number', value, onEnter: save,
        style: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: 0, fontFamily: wide ? 'inherit' : MONO, fontSize: wide ? '13.5px' : '14px' }
      }),
      unit && mono(unit, { key: 'u', fontSize: '11.5px', color: C.faint }))
  );
  const money = value => (value === null || value === undefined ? undefined : numberText(Math.round(value * 100) / 100));

  return h('div', {
    key: 'debtform-' + (editing ? editing.id : 'new') + '-' + ui.rev,
    id: 'debt-form', class: 'pt-rise',
    style: css({
      marginTop: '12px', padding: '14px', borderRadius: '12px', background: C.panel,
      border: `1px solid ${editing ? C.goldDeep : C.stroke}`, display: 'flex', flexDirection: 'column', gap: '12px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
      mono(editing ? s.dbFormEdit : s.dbFormNew, { fontSize: '10.5px', letterSpacing: '1.2px', color: editing ? C.gold : C.muted }),
      h('div', { style: css({ flex: 1 }) }),
      h('div', {
        ...tap(close, s.cfClose), class: 'pt-button', title: s.cfClose,
        style: css({ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.muted, fontSize: '16px', cursor: 'pointer' })
      }, '×')),
    segmented([['loan', s.dbKindLoan], ['card', s.dbKindCard]].map(([value, label]) => ({ value, label })),
      kind, value => actions.setDebtUi({ kind: value }), { key: 'kind' }),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 130 : 150}px, 1fr))`, gap: '10px' }) },
      field('dbName', s.dbLender, editing ? editing.name : undefined, null, { wide: true }),
      field('dbBal', editing ? s.debtBalanceIn(fmt.monthName(state.month)) : s.dbBalNow, editing ? money(shown) : undefined, null),
      field('dbOrig', s.dbOrig, editing ? money(editing.original) : undefined, null),
      field('dbRate', s.dbRateF, editing ? money(editing.rate) : undefined, '%'),
      field('dbRata', kind === 'card' ? s.dbCardRataF : s.dbRataF, editing && editing.payment ? money(editing.payment) : undefined, null),
      kind === 'loan' && field('dbMonths', s.dbMonthsF, editing && editing.term ? String(editing.term) : undefined, null),
      field('dbDay', s.dbPayDayF, editing && editing.payDay ? String(editing.payDay) : undefined, null),
      field('dbFee', s.dbFeeF, editing && editing.fees ? money(editing.fees) : undefined, null),
      h('label', { key: 'cur', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.dbCurrencyF),
        select(inputs, 'dbCur', currencyOptions(state, editing ? editing.currency : actions.entryCurrency()),
          { height: '42px', borderRadius: '10px', fontFamily: MONO, fontSize: '13px' },
          { value: editing ? editing.currency : actions.entryCurrency() }))
    ),
    h('div', { style: css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }) },
      h('div', { style: css({ flex: '1 1 300px', fontSize: '12px', lineHeight: 1.5, color: C.muted }) }, kind === 'card' ? s.dbCardNote : t.fx.debtFormNote),
      h('div', {
        ...tap(close, s.cfCancel),
        style: css({ height: '40px', display: 'flex', alignItems: 'center', padding: '0 14px', borderRadius: '9px', color: C.muted, fontSize: '13px', cursor: 'pointer' })
      }, s.cfCancel),
      pill(editing ? s.cfSaveChanges : s.dbAdd, save, { primary: true, key: 'save' }))
  );
}

// ---------------------------------------------------------------- plan ---

export function debtPlanCard({ metrics, t, fmt, state, actions, compact = false, plans }) {
  const s = t.shell;
  /* Today's debts, not the month on screen's: the plan is about what is still to pay. */
  const { base, plan, today, queue: rows } = plans || debtPlans(metrics, state);
  if (!rows.length) return null;
  const strategy = state.settings?.debtStrategy === 'balance' ? 'balance' : 'rate';
  const monthOf = n => fmt.monthName(shiftMonth(today, n));
  const extra = state.debtExtra || 0;
  const noRata = rows.filter(row => !(row.payment > 0));
  const saved = base.months !== null && plan.months !== null ? base.months - plan.months : null;
  const note = base.months === null && noRata.length
    ? s.dbNoteNoRata(noRata.map(row => row.name).join(', '), noRata.length)
    : saved !== null && saved > 0
      ? s.dbNoteSave(saved, fmt.money(base.interest - plan.interest))
      : s.dbNoteMove;

  return box(compact, {},
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) }, title(compact, s.dbPlan), hint(s.dbPlanSub)),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '20px 32px', alignItems: 'start' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }) },
        segmented(['rate', 'balance'].map(key => ({ value: key, label: s.dbStrat[key][0], sub: s.dbStrat[key][1] })),
          strategy, value => actions.setSettings({ debtStrategy: value }), { grow: true, key: 'strategy' }),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
          h('div', { style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }) },
            h('span', { style: css({ fontSize: '13px', color: C.textDim }) }, s.dbExtra),
            mono('+' + fmt.money(extra), { fontSize: '15px', fontWeight: 700, color: C.accent })),
          h('input', {
            type: 'range', min: '0', max: '1500', step: '50', value: String(extra), 'aria-label': s.dbExtra,
            oninput: event => actions.setDebtExtra(event.target.value),
            style: css({ width: '100%', accentColor: C.accent, margin: 0, height: compact ? '44px' : '24px' })
          })),
        h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }) },
          h('div', { style: css({ padding: '12px 14px', borderRadius: '10px', background: GAIN_BG, border: `1px solid ${GAIN_LINE}`, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }) },
            mono(s.dbFree, { fontSize: '10.5px', letterSpacing: '1.2px', color: GAIN_LABEL }),
            mono(plan.months === null ? s.dbNever : monthOf(plan.months), { fontSize: compact ? '16px' : '19px', fontWeight: 700, color: C.accentHi }),
            h('span', { style: css({ fontSize: '11.5px', color: '#B4CCC4' }) }, s.dbBaseFree(base.months === null ? s.dbNever : monthOf(base.months)))),
          h('div', { style: css({ padding: '12px 14px', borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}`, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }) },
            mono(s.dbIntTotal, { fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }),
            mono(plan.months === null ? '—' : fmt.money(plan.interest), { fontSize: compact ? '16px' : '19px', fontWeight: 700 }),
            h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, base.months === null ? s.dbBaseIntNever : s.dbBaseInt(fmt.money(base.interest)))))
      ),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }) },
        mono(s.dbOrder, { fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) },
          ...rows.map((row, i) => {
            const m = plan.paidAt[row.id];
            return h('div', {
              key: row.id,
              style: css({ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr) auto', gap: '10px', alignItems: 'center', minHeight: '40px', borderBottom: `1px solid ${C.line}` })
            },
              mono(String(i + 1), { fontSize: '11px', color: C.muted }),
              h('span', { style: css({ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, row.name),
              mono(m === undefined ? '—' : m === 0 ? s.dbPaidOffShort : monthOf(m), { fontSize: '12px', color: m === undefined ? C.gold : C.textDim }));
          })),
        h('div', { style: css({ fontSize: '12px', lineHeight: 1.5, color: C.muted }) }, note)
      )
    )
  );
}
